/**
 * Errors of the users domain, and the error codes the API sends for them.
 *
 * No dependencies: the frontend imports the codes (via `/schemas`) without
 * pulling in the repository and its AWS/Supabase clients.
 *
 * `UserAlreadyRegisteredError.message` is **user-facing**: the API returns it
 * as the `error` of the 409 response, so it is written in German and must not
 * carry a Postgres or driver message. (`UserNotFoundError`'s message is not
 * surfaced; the handlers answer with a fixed text.)
 */

/** Machine-readable `code` in error responses of the users API. */
export const UserErrorCodes = {
  /** GET /users/current: signed-in Cognito user without a local user */
  NotRegistered: "USER_NOT_REGISTERED",
  /** Registration: a user with that id exists already */
  AlreadyRegistered: "USER_ALREADY_REGISTERED",
} as const

export type UserErrorCode = (typeof UserErrorCodes)[keyof typeof UserErrorCodes]

/**
 * Thrown by `getById` if no user with that id exists.
 *
 * Deliberately without a `code`: whether a missing user means "not
 * registered" depends on the endpoint, see `UserErrorCodes.NotRegistered`.
 */
export class UserNotFoundError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "UserNotFoundError"
  }
}

/** Thrown on registration if a user with that id exists already. */
export class UserAlreadyRegisteredError extends Error {
  readonly code = UserErrorCodes.AlreadyRegistered

  constructor(message: string) {
    super(message)
    this.name = "UserAlreadyRegisteredError"
  }
}
